import { MONTH_LONG_DATE_SHORT_YEAR_TIME_FORMAT } from '@openphone/lib/date'
import dayjs from 'dayjs'

/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { FilterType as CallsViewFilterType } from '@src/app/inbox/calls/filters/CallsFiltersController'
/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { InboxFilterType } from '@src/app/inbox/conversations/InboxFilters'
/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { InboxState } from '@src/app/inbox/conversations/InboxUiStore'

// eslint-disable-next-line import-x/no-restricted-paths
import { OdysseyAnalyticsBridge } from '@odyssey/analytics/OdysseyAnalyticsBridge'

export default class InboxAnalyticsStore {
  private readonly analytics: typeof OdysseyAnalyticsBridge

  constructor() {
    this.analytics = OdysseyAnalyticsBridge
  }

  /**
   * Tracks when a user has sent a message
   */
  messageSent() {
    window.dataLayer.push({
      event: 'message_sent',
    })
  }

  messageScheduled(action: 'click' | 'submit') {
    void this.analytics.track('message_scheduled', { action })
  }

  inboxFiltered(filter: InboxState): void
  inboxFiltered(filter: InboxFilterType, totalFilters: number): void
  inboxFiltered(filter: string, totalFilters?: number): void {
    void this.analytics.track('inbox_filtered', { filter, total_filters: totalFilters })
  }

  inboxFilterCleared(filter: InboxState | InboxFilterType, totalFilters: number) {
    void this.analytics.track('inbox_filter_cleared', {
      filter,
      total_filters: totalFilters,
    })
  }

  inboxFiltersCollapsed(totalFilters: number) {
    void this.analytics.track('inbox_filters_collapsed', { total_filters: totalFilters })
  }

  inboxFiltersExpanded(totalFilters: number) {
    void this.analytics.track('inbox_filters_expanded', { total_filters: totalFilters })
  }

  conversationPinned(pinCount: number, conversationId: string, inboxId: string | null) {
    void this.analytics.track('conversation_pinned', {
      pinCount,
      conversationId,
      inboxId,
    })
  }

  conversationUnpinned(pinCount: number, conversationId: string, inboxId: string | null) {
    void this.analytics.track('conversation_unpinned', {
      pinCount,
      conversationId,
      inboxId,
    })
  }

  conversationRenamed() {
    void this.analytics.track('conversation_renamed')
  }

  conversationRenameInitiated(trigger: 'header_button' | 'action' | 'double_click_name') {
    void this.analytics.track('conversation_rename_initiated', { trigger })
  }

  /**
   * Based on the action:
   * `clicked` Corresponds with Web - Opened - AI Message Response
   * `selected` Corresponds with Web - Selected - AI Message Response
   */
  aiMessageResponseActioned(action: 'hovered' | 'clicked' | 'submitted' | 'selected') {
    void this.analytics.track('AI_message_response_actioned', { action })
  }

  callTranscriptOpened(): void {
    const timestamp = this.getTimestamp()
    void this.analytics.track('call_transcript_opened', {
      timestamp,
    })
  }

  /**
   * Tracks when a user performs a search within a call transcript.
   *
   * Corresponds with Web
   */
  transcriptSearchUsed(): void {
    void this.analytics.track('transcript_search_used')
  }

  /**
   * User started playback from a call transcript line timestamp.
   */
  transcriptTimestampPlay(startSeconds: number): void {
    void this.analytics.track('transcript_timestamp_play', {
      start_seconds: startSeconds,
    })
  }

  callAiSummaryExpanded(): void {
    const timestamp = this.getTimestamp()
    void this.analytics.track('call_ai_summary_expanded', {
      timestamp,
    })
  }

  callAiSummaryCollapsed(): void {
    const timestamp = this.getTimestamp()
    void this.analytics.track('call_ai_summary_collapsed', {
      timestamp,
    })
  }

  private getTimestamp() {
    return dayjs(Date.now()).format(MONTH_LONG_DATE_SHORT_YEAR_TIME_FORMAT)
  }

  /**
   * Tracks when a user has downloaded an image
   */
  imageDownloaded() {
    // Corresponds with Web - Downloaded - Image
    void this.analytics.track('image_downloaded')
  }

  /**
   * Tracks when a user creates a new snippet
   */
  snippetCreated() {
    void this.analytics.track('snippet_created')
  }

  /**
   * Tracks when a user uses a new snippet
   */
  snippetUsed() {
    void this.analytics.track('snippet_used')
  }

  /**
   *
   * Tracks when a internal thread message is sent
   * @param phoneNumberId
   */
  internalThreadMessageSent(phoneNumberId: string) {
    void this.analytics.track('internal_thread_message_sent', { phoneNumberId })
  }

  /**
   * Tracks when a internal thread message started getting edited
   */
  internalThreadMessageEditClicked() {
    void this.analytics.track('internal_thread_message_edit_click')
  }

  /**
   * Tracks when a user applies a filter in calls view
   * @param filter Filter applied from the set of filters that can be applied
   * @param count Total number of filters applied
   */
  callViewsFiltered(filter: CallsViewFilterType, count: number) {
    void this.analytics.track('call_views_filtered', { filter, filter_count: count })
  }

  /**
   * Tracks when a user removes a filter in calls view
   * @param filter Filter removed from the set of filters that are applied
   * @param count Total number of filters applied
   */
  callViewsCleared(filter: CallsViewFilterType, count: number) {
    void this.analytics.track('call_views_cleared', { filter, filter_count: count })
  }

  /**
   * Tracks when a user clicks on a row from the calls list
   */
  callViewsCallRowClicked() {
    void this.analytics.track('call_views_clicked_conversation')
  }

  /**
   * Tracks when a user clicks on the button to play a recording from a row
   */
  callViewsRecordingPlayed() {
    void this.analytics.track('call_views_played_recording')
  }

  /**
   * Tracks when a user clicks on the button to copy a conversation as markdown
   */
  conversationCopiedAsMarkdown(conversationId: string) {
    void this.analytics.track('conversation_copied_as_markdown', { conversationId })
  }

  /**
   * Tracks when a user marks a spam-flagged conversation as not spam.
   * @param source Where the action was triggered from — the conversation
   * actions menu or the spam callout in the activity view.
   */
  spamMarkedNotSpam(source: 'actions_popover' | 'spam_callout') {
    void this.analytics.track('spam_marked_not_spam', { source })
  }

  /**
   * Tracks when a user blocks the sender of a spam-flagged conversation from
   * the spam callout in the activity view.
   */
  spamSenderBlocked() {
    void this.analytics.track('spam_sender_blocked')
  }

  toggleDoneViaShortcut(shortcut: string, context: 'conversation' | 'bulk') {
    void this.analytics.track('inbox_toggle_done_via_shortcut', { shortcut, context })
  }

  toggleReadViaShortcut(shortcut: string, context: 'conversation' | 'bulk') {
    void this.analytics.track('inbox_toggle_read_via_shortcut', { shortcut, context })
  }

  makeCallViaShortcut(shortcut: string) {
    void this.analytics.track('inbox_make_call_via_shortcut', { shortcut })
  }

  commandPaletteOpenedViaShortcut(shortcut: string) {
    void this.analytics.track('inbox_command_palette_opened_via_shortcut', { shortcut })
  }

  searchOpenedViaShortcut(shortcut: string) {
    void this.analytics.track('inbox_search_opened_via_shortcut', { shortcut })
  }

  bulkSelectionClearedViaShortcut(shortcut: string) {
    void this.analytics.track('inbox_bulk_selection_cleared_via_shortcut', { shortcut })
  }

  bulkSelectAllViaShortcut(shortcut: string) {
    void this.analytics.track('inbox_bulk_select_all_via_shortcut', { shortcut })
  }

  bulkSelectionExtendedViaShortcut(shortcut: string) {
    void this.analytics.track('inbox_bulk_selection_extended_via_shortcut', { shortcut })
  }
}
